import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  ProgressStepper,
  Step,
  StepButton,
  StepLabel,
} from '@one-thd/sui-atomic-components';
import { RadioButtonUnchecked } from '@one-thd/sui-icons';
import { SweepstakesMarkdown } from '../../../utils/SweepstakesMarkdown';
import { SWEEPSTAKES_DETAILS_CONTENT } from '../../../../../core/Data/SweepstakesMockData';
import { windowExists } from '../../../../../common/utils/AccountUtils';
import { useImageError } from '../../../utils/SweepstakesUtils';
import { RenderCompletionAlert } from '../../SweepstakesUtils';

// Utility functions
const handleStepClick = (step, sweepstakesData) => {
  if (windowExists()) {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-step-drawer', {
      selectedStep: step,
      sweepstakesData,
    });
  }
};

// Render components
const RenderStepButton = ({ step, index, onStepClick }) => {
  const stepIcon = useImageError();
  return (
    <Step key={step.id} completed={step.completed}>
      <div data-testid={`sweepstakes-step-button-${step.id}`}>
        <StepButton
          onClick={() => onStepClick(step)}
          icon={() => (
            stepIcon.hasError ? (
              <RadioButtonUnchecked />
            ) : (
              <img
                src={step.iconUrl}
                alt={`Step ${index + 1}`}
                width={28}
                height={28}
                className="sui-rounded-full"
                onError={stepIcon.handleError}
              />
            )
          )}
        />
      </div>
      <StepLabel>
        <div>
          <Typography variant="body-base" decoration="underline" onClick={() => onStepClick(step)}>
            {step.title}
          </Typography>
          {step.subtitle && (
            <div className="sui-mt-1">
              <Typography variant="body-sm" color="subtle">
                {step.subtitle}
              </Typography>
            </div>
          )}
        </div>
      </StepLabel>
    </Step>
  );
};

const RenderStepsSection = ({ steps, onStepClick }) => {
  if (!steps || steps.length === 0) return null;

  return (
    <div>
      <div className="sui-mb-4">
        <Typography variant="body-lg" weight="bold">
          {SWEEPSTAKES_DETAILS_CONTENT.stepsHeader}
        </Typography>
      </div>
      <ProgressStepper
        centerEdgeNodes
        nonLinear
        orientation="vertical"
        connector={<></>}
      >
        {steps.map((step, index) => (
          <RenderStepButton key={step.id} step={step} index={index} onStepClick={onStepClick} />
        ))}
      </ProgressStepper>
    </div>
  );
};

const RenderFooter = ({ sweepstakesData }) => {
  if (!sweepstakesData?.ctaTitle) return null;

  return (
    <Button
      variant="primary"
      fullWidth
      data-testid="sweepstakes-details-cta-button"
    >
      {sweepstakesData.ctaTitle}
    </Button>
  );
};

export const DetailsContent = ({ sweepstakesData }) => {
  const onStepClick = React.useCallback((step) => {
    handleStepClick(step, sweepstakesData);
  }, [sweepstakesData]);

  const content = (
    <div className="sui-space-y-6" data-testid="sweepstakes-details-content">
      <div>
        <SweepstakesMarkdown markdownText={SWEEPSTAKES_DETAILS_CONTENT.initialParagraph} />
      </div>
      <RenderStepsSection steps={sweepstakesData?.steps} onStepClick={onStepClick} />
      <RenderCompletionAlert />
    </div>
  );

  const footer = <RenderFooter sweepstakesData={sweepstakesData} />;

  return { content, footer };
};

DetailsContent.propTypes = {
  sweepstakesData: PropTypes.shape({
    title: PropTypes.string,
    logo: PropTypes.string,
    ctaTitle: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      iconUrl: PropTypes.string,
      completed: PropTypes.bool,
    })),
  }),
};

DetailsContent.defaultProps = {
  sweepstakesData: null,
};